import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  InputAdornment,
  Snackbar,
  IconButton,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

const PendingReq = () => {
  const selectedManufacturer = JSON.parse(localStorage.getItem("selectedMfr"));
  const selectedDigitalMarketer = JSON.parse(localStorage.getItem("user"));
  const mfr_id = selectedManufacturer?.id;
  const dm_id = selectedDigitalMarketer?.id;
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [pendingReqs, setPendingReqs] = useState([]);
  const [filterResponse, setFilterResponse] = useState("action");
  const [activeLinks, setActiveLinks] = useState([]);
  const [filterLinkId, setFilterLinkId] = useState("all");
  const [filterDate, setFilterDate] = useState("");

  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const dateConverter = (dateString) => {
    if (!dateString) return "";
    let date = new Date(dateString);
    if (isNaN(date)) {
      const parts = dateString.split(" ");
      const [y, m, d] = parts[0].split("-");
      const [time, meridian] = parts[1] ? parts[1].split(" ") : ["00:00", "AM"];
      let [hours, minutes] = time.split(":").map(Number);
      if (meridian?.toLowerCase() === "pm" && hours < 12) hours += 12;
      if (meridian?.toLowerCase() === "am" && hours === 12) hours = 0;
      date = new Date(y, m - 1, d, hours, minutes);
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}-${month}-${year} at ${hours}:${minutes}`;
  };

  // ---------- FETCH PENDING REQUESTS ----------
  useEffect(() => {
    const fetchPendingReqs = async () => {
      try {
        const url = `${API_BASE_URL}/api/auth/temp-user-auth/${mfr_id}`;
        const { data } = await axios.get(url);
        if (data.statusCode === 200 && data?.data?.records) {
          setPendingReqs(data.data.records);
        }
      } catch (error) {
        console.error("Error fetching requests:", error);
      }
    };
    fetchPendingReqs();
  }, []);

  // ---------- FETCH ACTIVE LINKS ----------
  const getAllActiveLinks = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/auth/links/${mfr_id}`,
      );

      if (response.data?.statusCode !== 200) throw response.data;

      setActiveLinks(response.data.data || []);
    } catch (error) {
      console.error("Error fetching active links:", error);
    }
  };

  useEffect(() => {
    getAllActiveLinks();
  }, []);

  // ---------- FILTER LOGIC ----------
  const filteredRequests = useMemo(() => {
    const list = pendingReqs || [];

    return list.filter((req) => {
      const mobile = req?.mobile_number || "";
      const name = req?.user_name?.toLowerCase() || "";
      const linkId = req?.link_id;
      const createdAt = req?.created_at;

      const searchLower = searchTerm.toLowerCase();

      // 🔍 Search by mobile number OR user_name
      const matchSearch =
        mobile.includes(searchTerm) || name.includes(searchLower);

      // 🔗 Link filter
      const matchLink = filterLinkId === "all" ? true : filterLinkId === linkId;

      // 📅 Date Filter (From selected → Today)
      const matchDate = (() => {
        if (!filterDate) return true;

        const recordDate = new Date(createdAt);
        const rec = new Date(
          recordDate.getFullYear(),
          recordDate.getMonth(),
          recordDate.getDate(),
        ).getTime();

        const from = new Date(filterDate + "T00:00:00").getTime();

        const to = new Date();
        to.setHours(0, 0, 0, 0);
        const toDateOnly = to.getTime();

        return rec >= from && rec <= toDateOnly;
      })();

      return matchSearch && matchLink && matchDate;
    });
  }, [pendingReqs, searchTerm, filterLinkId, filterDate]);

  // ---------- ACTIONS ----------
  const handleAction = async (mobile_number, actionType, linkId) => {
    try {
      let url = "";
      let method = "";
      let body = {};

      if (actionType === "accept") {
        url = `${API_BASE_URL}/api/auth/accept-request`;
        method = "post";
        body = {
          mobile_number,
          mfr_id,
          linkId,
          dm_id,
        };
      } else if (actionType === "reject") {
        url = `${API_BASE_URL}/api/auth/temp-user-auth`;
        method = "delete";
        body = {
          mobile_number,
          mfr_id,
        };
      }

      const response =
        method === "delete"
          ? await axios.delete(url, { data: body })
          : await axios.post(url, body);

      if (response?.data?.statusCode === 200) {
        setSnackbar({
          open: true,
          message: `Request for ${mobile_number} ${
            actionType === "accept" ? "accepted ✅" : "rejected ❌"
          }`,
        });

        setPendingReqs((prev) =>
          prev.filter((req) => req.mobile_number !== mobile_number),
        );
      } else {
        setSnackbar({
          open: true,
          message: `Failed to ${actionType} request for ${mobile_number}`,
        });
      }
    } catch (error) {
      console.error(`${actionType} error:`, error);
      setSnackbar({
        open: true,
        message: `Error while ${actionType} request for ${mobile_number}`,
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "" });
  };

  return (
    <Box
      sx={{
        bgcolor: "#394A5C",
        minHeight: "100vh",
        width: "100%",
        p: { xs: 2, sm: 6 },
      }}
    >
      {/* NAVIGATE BACK & HOME BUTTONS */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 3,
            bgcolor: "#FFF",
            p: 0.5,
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: "#e0e0e0",
              "& svg": {
                fill: "#056b05ff",
              },
            },
          }}
        >
          <ArrowBackIosNewRoundedIcon
            onClick={() => navigate(-1)}
            sx={{
              fontSize: 20,
              fill: "#42A996",
              transition: "all 0.2s ease",
            }}
          />
        </Box>

        <Box
          sx={{
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 3,
            bgcolor: "#FFF",
            p: 0.5,
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: "#e0e0e0",
              "& svg": {
                fill: "#056b05ff",
              },
            },
          }}
        >
          <HomeRoundedIcon
            onClick={() => navigate("/admin-dash")}
            sx={{
              fontSize: 20,
              fill: "#42A996",
              transition: "all 0.2s ease",
            }}
          />
        </Box>
      </Box>

      {/* HEADING */}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            color: "#fff",
            fontSize: { xs: 22, sm: 28 },
            mt: 1,
            mb: 2,
            fontWeight: 600,
          }}
        >
          Pending Requests
        </Typography>
        <Button
          onClick={() => navigate("/createuser")}
          sx={{ bgcolor: "#3b927f", color: "#ffff", height: 32 }}
        >
          <Typography sx={{ fontSize: 12 }}>Create Customer</Typography>
        </Button>
      </Box>

      {/* FILTER SECTION */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        {/* Search */}
        <TextField
          placeholder="Search by Mobile No"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            width: { xs: "100%", sm: 220, md: 260 },
            bgcolor: "#fff",
            borderRadius: 2,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#667A8A" }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Response Filter */}
        <Select
          value={filterResponse}
          onChange={(e) => setFilterResponse(e.target.value)}
          size="small"
          sx={{
            width: { xs: "100%", sm: 200, md: 220 },
            bgcolor: "#fff",
            borderRadius: 2,
          }}
        >
          <MenuItem value="action">Action (Default)</MenuItem>
          <MenuItem value="accepted">Accepted</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
        </Select>

        {/* Link Filter */}
        <Box sx={{ width: { xs: "100%", sm: 200, md: 220 } }}>
          <select
            value={filterLinkId}
            onChange={(e) => setFilterLinkId(e.target.value)}
            style={{
              padding: "10px",
              borderRadius: "8px",
              background: "#fff",
              color: "#000",
              border: "none",
              width: "100%",
              appearance: "none",
            }}
          >
            <option value="all" style={{ background: "#fff", color: "#000" }}>
              All Links
            </option>

            {activeLinks.map((link) => (
              <option
                key={link.linkId}
                value={link.linkId}
                style={{ background: "#fff", color: "#000" }}
              >
                {link.name}
              </option>
            ))}
          </select>
        </Box>

        {/* Date Filter */}
        <TextField
          type="date"
          size="small"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          sx={{
            width: { xs: "100%", sm: 180, md: 200 },
            bgcolor: "#fff",
            borderRadius: 2,
          }}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {/* TABLE */}
      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          borderRadius: 2,
          bgcolor: "#2A3A4B",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "rgba(30,47,64,0.95)" }}>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Name
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Mobile No.
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Link Name
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Date & Time
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Response
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Reason
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredRequests.map((req, index) => (
              <TableRow
                key={index}
                sx={{ bgcolor: index % 2 === 0 ? "#1E2F40" : "#2A3A4B" }}
              >
                <TableCell sx={{ color: "#fff" }}>
                  {req.user_name || "—"}
                </TableCell>
                <TableCell sx={{ color: "#fff" }}>
                  {req.mobile_number}
                </TableCell>
                <TableCell sx={{ color: "#fff" }}>{req.link_name}</TableCell>
                <TableCell sx={{ color: "#fff" }}>
                  {dateConverter(req.created_at)}
                </TableCell>
                <TableCell sx={{ color: "#fff" }}>{req.response}</TableCell>
                <TableCell sx={{ color: "#fff" }}>
                  Does not belong to the private group
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={() =>
                      handleAction(req.mobile_number, "accept", req.link_id)
                    }
                    sx={{ color: "#42A996" }}
                  >
                    <CheckCircleIcon />
                  </IconButton>

                  <IconButton
                    onClick={() => handleAction(req.mobile_number, "reject")}
                    sx={{ color: "#f44336" }}
                  >
                    <CancelIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default PendingReq;
